import { useEffect, useRef, useState } from 'react';

/**
 * Animated count-up number, triggered once when scrolled into view.
 * Eases out (quart) and honors prefers-reduced-motion by showing the final
 * value immediately. Pass a numeric `value`; optional `prefix` / `suffix`
 * (e.g. suffix="+") and `duration` in ms.
 */
export default function StatCounter({
  value,
  duration = 1700,
  prefix = '',
  suffix = '',
  className = '',
}) {
  const target = Number(value) || 0;
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setDisplay(target);
      return undefined;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const step = (now) => {
              const p = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - p, 4); // ease-out-quart
              setDisplay(Math.round(target * eased));
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}
